import React, { Fragment, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import cn from 'classnames';
import { includes } from 'lodash';
import { useMergeRefs } from 'use-callback-ref';

import {
    BetslipAttrManager,
    CompareStatus,
    ODD_LOSS_STATUSES,
    ODD_WIN_STATUSES,
} from 'app-constants';
import { Icon } from 'components';
import globalMsgs from 'messages';
import {
    Action,
    Category,
    formatPathname,
    formatSearch,
    useAnalyticAttrs,
} from 'services/analytics';
import { formatDecimalOdd } from 'utils';
import useOddButton from './useOddButton';

import arrow from 'assets/svg/arrow.svg';
import './oddButton.scss';

type Btn = React.DOMAttributes<HTMLDivElement>;

interface Props {
    oddId: string;
    onMouseEnter?: Btn['onMouseLeave'];
    onMouseLeave?: Btn['onMouseEnter'];
}

const MAX_FLATTEN_HEIGHT = 40 as const;

const OddButton: React.RefForwardingComponent<HTMLDivElement, Props> = (
    { oddId, onMouseEnter, onMouseLeave },
    outerRef
) => {
    const { search, pathname, hash } = useLocation();
    const { formatMessage } = useIntl();
    const innerRef = useRef<HTMLDivElement>(null);

    const isFlattenedView =
        (innerRef.current?.clientHeight || Infinity) < MAX_FLATTEN_HEIGHT;

    const mergeRefs = useMergeRefs<any>([outerRef, innerRef]);

    const {
        isActive,
        isSelected,
        value,
        title,
        titleAttr,
        compareStatus,
        handleToggleOdd,
        statistic,
        oddFormat,
        status,
        betslipOddId,
    } = useOddButton({
        oddId,
    });

    const analytic = useAnalyticAttrs({
        defaultAttrs: { 'data-category': Category.Unknown },
        priorityAttrs: {
            'data-action': Action.SelectOdd,
            'data-label': betslipOddId,
        },
    });

    const isWin = includes(ODD_WIN_STATUSES, status);
    const isLoss = includes(ODD_LOSS_STATUSES, status);

    const { component: CoefEl } = isActive
        ? formatDecimalOdd(value, oddFormat)
        : { component: '-' };

    const resultTitle = isWin
        ? formatMessage(globalMsgs.won)
        : isLoss
        ? formatMessage(globalMsgs.lost)
        : undefined;

    const ResultEl = <div styleName="coef">{resultTitle || CoefEl}</div>;
    const TitleEl = <div styleName="title">{title}</div>;
    const StatusEl = (compareStatus !== CompareStatus.Equal || statistic) && (
        <div styleName="status">
            {isActive && compareStatus !== CompareStatus.Equal ? (
                <Fragment>
                    <Icon
                        glyph={arrow}
                        styleName={cn('arrow', {
                            greater: compareStatus === CompareStatus.Greater,
                            smaller: compareStatus === CompareStatus.Smaller,
                        })}
                    />
                </Fragment>
            ) : (
                statistic && <div styleName="statistic">{statistic}%</div>
            )}
        </div>
    );

    const TopEl = (
        <div styleName="top">
            {ResultEl}
            {StatusEl}
        </div>
    );

    const BottomEl = <div styleName="bottom">{TitleEl}</div>;

    const FlattenEl = (
        <div styleName="flatten">
            {ResultEl}
            {TitleEl}
            {StatusEl}
        </div>
    );

    const onClick: DivEl['onClick'] = e => {
        e.preventDefault();

        if (!isActive && !isSelected) return;

        handleToggleOdd();

        if (!betslipOddId) return;

        if (isSelected) {
            delete BetslipAttrManager[betslipOddId];

            return;
        }

        const selectUrl = `${formatPathname(pathname)}${formatSearch(
            search
        )}${hash}`;

        BetslipAttrManager[betslipOddId] = {
            category: analytic['data-category'],
            label: { selectUrl },
        };
    };

    return (
        <div
            ref={mergeRefs}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            title={titleAttr}
            styleName={cn('container', {
                'is-active': isActive,
                'is-selected': isSelected,
                'is-flattened': isFlattenedView,
                'is-statistic-exist': !!statistic,
                'is-win': isWin,
                'is-loss': isLoss,
            })}
            onClick={onClick}
            {...analytic}
        >
            {isFlattenedView ? (
                FlattenEl
            ) : (
                <Fragment>
                    {TopEl}
                    {BottomEl}
                </Fragment>
            )}
        </div>
    );
};

export default React.forwardRef(OddButton);
